import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate, useBlocker } from "react-router-dom";
import {
  useESGReportBuilderStore,
  useESGDataEntryStore,
  useESGDisclosureSelectionStore,
  useESGReportWorkflowStore,
  useESGCollaborationStore,
} from "@/features/esg/hooks/useESGReportStore";
import { useTran } from "@/hooks";
import { Calendar, ArrowLeft, CheckCircle, Check, Loader2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { PATHS } from "@/lib/router";
import { showToast } from "@/lib/utils/toast";
import { AppDialog } from "@/components/common";

const ESGReportWizardHeader = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const {
    isNew,
    framework,
    reportYear,
    createReport,
    updateReport,
    orgName,
    reportName,
    isDirty: reportDirty,
  } = useESGReportBuilderStore();
  const {
    currentStep,
    steps,
    canGoNext,
    canGoPrevious,
    canSubmit,
    nextStep,
    previousStep,
  } = useESGReportWorkflowStore();
  const {
    progressPercentage,
    isDirty: dataDirty,
    isSaving,
    lastSaved,
    cancelAutoSave,
  } = useESGDataEntryStore();
  const {
    selectedCodes,
    customDisclosures,
    isDirty: selectionDirty,
  } = useESGDisclosureSelectionStore();
  const { delegatedDisclosures } = useESGCollaborationStore();

  const needsSave = reportDirty || selectionDirty || dataDirty;

  const blocker = useBlocker(needsSave);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
    };
    if (needsSave) {
      window.addEventListener("beforeunload", handler);
    }
    return () => window.removeEventListener("beforeunload", handler);
  }, [needsSave]);

  const [isSavingBeforeLeave, setIsSavingBeforeLeave] = useState(false);

  const handleSaveAndLeave = async () => {
    setIsSavingBeforeLeave(true);
    try {
      cancelAutoSave();
      const data = isNew ? await createReport() : await updateReport();
      if (data) {
        blocker.proceed();
      } else {
        showToast.error(t("Failed to save report"));
      }
    } catch {
      showToast.error(t("Failed to save report"));
    } finally {
      setIsSavingBeforeLeave(false);
    }
  };

  const isFinishReady =
    currentStep === 4 &&
    progressPercentage === 100 &&
    (selectedCodes.length > 0 || delegatedDisclosures.size > 0);

  const handleFinish = async () => {
    cancelAutoSave();
    if (!reportName) {
      showToast.error(t("Please enter a report name before finishing"));
      return;
    }
    if (selectedCodes.length === 0 && delegatedDisclosures.size === 0) {
      showToast.error(
        t("Please select at least one disclosure before finishing"),
      );
      return;
    }

    try {
      if (isNew) {
        await createReport();
      } else {
        await updateReport();
      }
      showToast.success(t("Report completed successfully!"));
      navigate(PATHS.ESG_REPORTS);
    } catch (error) {
      showToast.error(error?.message || t("Failed to finish report"));
    }
  };

  const handleNext = async () => {
    const hasSelections =
      selectedCodes.length > 0 || customDisclosures.length > 0;
    const hasDelegations = delegatedDisclosures.size > 0;

    if (currentStep === 1) {
      if (!isNew && needsSave) {
        const data = await updateReport();
        if (!data) {
          return;
        }
      }
    }

    if (currentStep === 2) {
      if (!hasSelections && !hasDelegations) {
        showToast.error(t("Please select at least one disclosure to continue"));
        return;
      }
      if (isNew && needsSave) {
        const data = await createReport();
        if (!data) {
          return;
        }
      } else if (!isNew && needsSave) {
        const data = await updateReport();
        if (!data) {
          return;
        }
      }
    }

    if (currentStep === 3) {
      cancelAutoSave();
      if (needsSave) {
        const data = await updateReport();
        if (!data) {
          return;
        }
      }
    }
    nextStep();
  };

  return (
    <div className="flex items-center gap-3 justify-between">
      <div className="flex items-center gap-x-2">
        <Button variant="ghost" onClick={() => navigate(PATHS.ESG_REPORTS)}>
          <ArrowLeft />
          {t("Back to Reports")}
        </Button>
        <h3 className="text-lg text-secondary dark:text-primary font-semibold">
          {t(`ESG Report - ${framework} (Step ${currentStep}/${steps.length})`)}
        </h3>
        <Badge className="bg-muted text-muted-foreground rounded-md">
          {`Org: ${orgName}`}
        </Badge>
      </div>

      <div className="flex items-center gap-x-2">
        <Badge variant="outline">
          <Calendar />
          {reportYear}
        </Badge>
        {currentStep === 3 && (
          <span
            className={`text-xs flex items-center gap-1 ${
              isSaving
                ? "text-muted-foreground"
                : "text-green-600 dark:text-green-400"
            }`}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                {t("Saving...")}
              </>
            ) : lastSaved ? (
              <>
                <Check className="h-3 w-3" />
                {t("Saved")}
              </>
            ) : null}
          </span>
        )}
        {canGoPrevious && (
          <Button variant="outline" onClick={previousStep}>
            {t("Previous")}
          </Button>
        )}
        {canGoNext && (
          <Button size="sm" onClick={handleNext}>
            {t("Next")}
          </Button>
        )}
        {canSubmit && isFinishReady ? (
          <Button onClick={handleFinish} className="gap-2">
            <CheckCircle className="h-4 w-4" />
            {t("Finish Report")}
          </Button>
        ) : null}
      </div>
      <AppDialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) =>
          !open && blocker.state === "blocked" && blocker.reset()
        }
        title={t("Unsaved Changes")}
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                cancelAutoSave();
                blocker.proceed();
              }}
              disabled={isSavingBeforeLeave}
            >
              {t("Discard")}
            </Button>
            <Button onClick={handleSaveAndLeave} disabled={isSavingBeforeLeave}>
              {isSavingBeforeLeave ? t("Saving...") : t("Save")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          {t("You have unsaved changes that will be lost if you leave.")}
        </p>
      </AppDialog>
    </div>
  );
});

export default ESGReportWizardHeader;
